import {
  collection,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './config';

const ledgerRef = collection(db, 'trainingLedger');

// Live ledger for a specific horse. Read access is enforced by the security
// rule (must be a member of the horse), so we can simply query by horseId.
// On a transient snapshot error we intentionally do NOT clear the data — the
// last-known entries stay on screen rather than flashing the balance to $0.
// Ledger across several horses at once, for an invoice that covers more than
// one. Firestore takes at most 10 values in an `in` query, so chunk and merge —
// the same shape subscribeToHerdIncidents uses.
export const subscribeToLedgerForHorses = (horseIds, callback) => {
  const ids = (horseIds || []).filter(Boolean);
  if (ids.length === 0) { callback([]); return () => {}; }
  const chunks = [];
  for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));
  const results = chunks.map(() => []);
  // Each chunk is its own listener and they arrive independently, so nothing is
  // reported until every one of them has answered once. A caller that acted on
  // a partial list — pre-ticking a client's horses, say — would silently miss
  // the horses whose chunk had not landed yet. A chunk that fails counts as
  // answered, so one broken query cannot hold back the rest for ever.
  const answered = chunks.map(() => false);
  const emit = () => { if (answered.every(Boolean)) callback(results.flat()); };
  const unsubs = chunks.map((chunk, idx) =>
    onSnapshot(query(ledgerRef, where('horseId', 'in', chunk)), (snap) => {
      results[idx] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      answered[idx] = true;
      emit();
    }, () => {
      // Keep whatever this chunk last held rather than blanking it.
      answered[idx] = true;
      emit();
    })
  );
  return () => unsubs.forEach((u) => u());
};

export const subscribeToLedger = (horseId, callback) => {
  if (!horseId) { callback([]); return () => {}; }
  const q = query(ledgerRef, where('horseId', '==', horseId));
  return onSnapshot(q, (snap) => {
    const entries = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    entries.sort((a, b) => {
      const dateCmp = (b.date || '').localeCompare(a.date || '');
      if (dateCmp !== 0) return dateCmp;
      const aMs = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
      const bMs = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
      return bMs - aMs;
    });
    callback(entries);
  }, () => { /* keep last-known state on transient error */ });
};

// Add a charge line item (trainer only). `provider` = { name, type } stamps who
// billed it, so entries group by provider even if that provider is later removed.
export const addCharge = (horseId, trainerUid, ownerUid, description, amount, date, provider = null, note = '') =>
  addDoc(ledgerRef, {
    horseId,
    trainerUid: trainerUid || null,
    ownerUid: ownerUid || null,
    type: 'charge',
    description: description.trim(),
    note: (note || '').trim(),
    amount,
    date,
    providerName: provider?.name || '',
    providerType: provider?.type || '',
    createdAt: serverTimestamp(),
  });

// Record a payment received (trainer only). `allocations` optionally applies
// the payment to specific charges: [{ chargeId, amount }]. Unallocated
// remainder is treated as general credit toward the balance.
export const addPayment = (horseId, trainerUid, ownerUid, amount, date, note, allocations = [], provider = null) =>
  addDoc(ledgerRef, {
    horseId,
    trainerUid: trainerUid || null,
    ownerUid: ownerUid || null,
    type: 'payment',
    description: note?.trim() || 'Payment received',
    amount,
    date,
    allocations: Array.isArray(allocations) ? allocations : [],
    providerName: provider?.name || '',
    providerType: provider?.type || '',
    createdAt: serverTimestamp(),
  });

// Edit an entry's description/amount/date. Any trainer of the horse may edit
// any entry — trainers on a horse are treated as one team.
export const updateLedgerEntry = (entryId, data) =>
  updateDoc(doc(db, 'trainingLedger', entryId), { ...data, updatedAt: serverTimestamp() });

// Stamp a charge with the invoice it was billed on (or '' to release it when an
// invoice is voided). Once stamped, the charge shows as "previously invoiced"
// and is never itemized as new again — so an item can't be billed on two
// invoices.
// Every invoice a charge has appeared on, oldest first. An unpaid charge is
// billed once and then carried forward onto each later invoice, so one number
// was never enough: voiding the first invoice used to release a charge that was
// still printed on a newer one the client is holding.
//
// Charges written before this field exists read as their single number, so old
// data behaves exactly as it always did.
export const invoiceListOf = (entry) => {
  if (Array.isArray(entry?.invoiceNumbers)) return entry.invoiceNumbers.filter(Boolean);
  return entry?.invoiceNumber ? [entry.invoiceNumber] : [];
};

// The invoice a charge was FIRST billed on — what the "previously invoiced"
// line refers to, since that is where the client originally saw it.
export const firstInvoiceOf = (entry) => invoiceListOf(entry)[0] || '';

// `invoiceNumber` keeps mirroring the most recent invoice, so everything that
// reads it — "is this charge new?", the Invoiced badge — carries on working.
const writeInvoiceList = (entryId, list, stamped) =>
  updateDoc(doc(db, 'trainingLedger', entryId), {
    invoiceNumbers: list,
    invoiceNumber: list.length ? list[list.length - 1] : '',
    ...(stamped ? { invoicedAt: serverTimestamp() } : {}),
    updatedAt: serverTimestamp(),
  });

export const addChargeToInvoice = async (entryId, num) => {
  if (!num) return;
  const snap = await getDoc(doc(db, 'trainingLedger', entryId));
  const cur = invoiceListOf(snap.data());
  if (cur.includes(num)) return; // already on this invoice
  await writeInvoiceList(entryId, [...cur, num], true);
};

// Take a charge off ONE invoice. It falls back to the previous invoice it was
// on rather than becoming new again — that older invoice is still outstanding.
export const removeChargeFromInvoice = async (entryId, num) => {
  const snap = await getDoc(doc(db, 'trainingLedger', entryId));
  const cur = invoiceListOf(snap.data());
  if (!cur.includes(num)) return;
  await writeInvoiceList(entryId, cur.filter((n) => n !== num), false);
};

// "Delete" an entry — a recoverable soft-delete so financial records are never
// lost by accident. Hidden from the ledger and excluded from balances/invoices,
// but restorable. Records who deleted it. Any trainer of the horse may do this.
export const deleteLedgerEntry = (entryId, byUid = null, byEmail = null) =>
  updateDoc(doc(db, 'trainingLedger', entryId), {
    deleted: true,
    deletedAt: serverTimestamp(),
    ...(byUid && { deletedBy: { uid: byUid, email: byEmail || '' } }),
    updatedAt: serverTimestamp(),
  });

// Restore a soft-deleted entry back into the ledger.
export const restoreLedgerEntry = (entryId) =>
  updateDoc(doc(db, 'trainingLedger', entryId), {
    deleted: false,
    deletedAt: null,
    updatedAt: serverTimestamp(),
  });
